/**
 * A diagram of Wend's parts and what moves between them — boxes on a dot grid,
 * joined by arrows, with a few labelled. The concept's "Loop" (`.bp-loop-map`,
 * `.bp-node`, `.bp-edge*`), made data-driven so the marketing site and, one
 * day, the product can draw the same picture from different facts.
 *
 * ## WHY IT IS DATA, NOT A DRAWING
 *
 * The concept hard-coded six boxes and nine arcs, several of them describing
 * things that do not exist — "suppress converted · if approved", "pause · move
 * budget". A picture is a claim, and the honest version of this one changes as
 * parts ship. So the component knows nothing about Reach or Sense: the caller
 * passes boxes in viewBox units, edge paths, and which edges carry a label,
 * and the component only draws. Whether an arrow may be drawn solid is the
 * caller's decision, written beside the facts that justify it (the site's
 * `Pillars.tsx` header says: solid only for data that moves today).
 *
 * ## TWO MODES, AND THE ONE THAT SHIPS
 *
 * `static` — what wendhq.com uses. The whole root is `aria-hidden`: there is no
 * button, no tabindex, no hover and no cursor, because a picture a screen
 * reader cannot use should not pretend to be a control. That puts a duty on
 * the caller, which this header states so it is not forgotten: **every fact
 * in the picture must also be in text on the page.** The site's lede and cards
 * carry them.
 *
 * `interactive` — nodes become `<button aria-pressed>`, the selected node and
 * its edges light up and the rest dim, arrow keys move between nodes, and
 * `renderDetail` draws the panel under the map that says in words what the
 * node receives and hands on. The svg stays `aria-hidden` in both modes; the
 * relationships are spoken through that panel. **It has no product consumer
 * today** — the tenant Overview deliberately does not draw this (owner
 * decision D4: it would show parts a workspace does not have). It is built
 * because it shares all of static mode's data, and `verify-ui-suite` exercises
 * it; if unused code is not wanted, drop the branch and keep the props.
 *
 * What the slice 66 review made it do in that mode, because each was a gap a
 * keyboard or screen-reader user would meet first:
 *
 *   - arrow keys work in the small-screen list too: its buttons carry
 *     `data-loop-node` as the map's do, and the key handler walks only the
 *     buttons that are rendered (the map is `display:none` below lg, and the
 *     list above it), so focus never jumps into a hidden copy;
 *   - the detail panel is a polite live region and every node points at it
 *     with `aria-controls`, so pressing a node is heard as more than
 *     "pressed";
 *   - a node's name comes before its kicker in the markup, so the button is
 *     "Sense, Next" and not "Next Sense" — `order-first` keeps the kicker on
 *     top visually;
 *   - the selected node takes an outline under forced colours, where its
 *     border and wash are replaced by system colours.
 *
 * ## SLICE 71: DIRECTION AND THE EDGE IN MOTION (interactive only, opt-in)
 *
 * wendhq.com now draws the concept's interactive loop, which says more than
 * "these edges touch the selected node": it says which way the data goes, and
 * the "Follow one person" stepper beside it lights the one edge the current
 * step moves data along. Two optional props carry that, and both default to
 * the behaviour above, so every existing caller renders byte for byte as it
 * did (`verify-ui-suite` section 8 is the proof):
 *
 *   - `emphasis="direction"` — edges leaving the selected node are lit in
 *     brand (what it hands on), edges arriving are lit in plain text colour
 *     (what it receives). The concept drew "in" in cyan; cyan is `info`, a
 *     status colour, so it is still not ported. Labels follow their edge.
 *   - `activeEdge` — one edge drawn white with marching dashes, the data
 *     "moving right now". The dash animation is declared in a `<style>` inside
 *     the svg (this package owns no keyframes for it in theme.css) and is
 *     applied only under `motion-safe:`, so a reader who asked for reduced
 *     motion gets a still, white, dashed edge — the same information.
 *
 * The extra markers those looks need are rendered only when a caller opts in,
 * for the same byte-for-byte reason.
 *
 * ## THE COLOURS
 *
 * Every node is `bg-surface-1`, so a `brand-b` mark sits on surface-1 (4.76:1)
 * and never on surface-2 (4.47:1). Solid edges are `brand-b`; dashed edges are
 * muted with a 3·7 dash. The concept's cyan "in" edges are not ported (cyan is
 * `info`, a status), nor its hand-mixed label surfaces (`#1d1544`, `#251b55`)
 * or the marching-ants animation. Node radius is `rounded-base` — the
 * concept's 14px snaps to the scale.
 *
 * Markers are per idPrefix (two loops on one page cannot collide on an `id`)
 * and per stroke colour — an svg marker cannot inherit its path's stroke in
 * every browser yet, so there is one marker per colour, each oriented
 * `auto-start-reverse` so it serves both ends of a two-way edge.
 *
 * ## THE HOLE IT LEAVES
 *
 * Node text is not measured against its box. A long kicker wraps inside the
 * box (the site's 1024px check is exactly this), but a box made too small for
 * its words will clip them; the caller sizes the boxes.
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { Kicker } from './Kicker';

export type LoopNodeState = 'available' | 'conditional' | 'later' | 'foundation';

export interface LoopBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface LoopNode {
  readonly id: string;
  readonly kicker: string;
  readonly name: string;
  readonly line?: string;
  readonly glyph?: ReactNode;
  readonly state: LoopNodeState;
  /** Position and size in viewBox units. */
  readonly box: LoopBox;
}

export interface LoopEdgeLabel {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  /** Which point of the label sits on (x, y). Default `'middle'`. */
  readonly anchor?: 'start' | 'end' | 'middle';
}

export interface LoopEdge {
  readonly id: string;
  /** An svg path in viewBox units. */
  readonly d: string;
  /** Solid: data moves today. Dashed: it does not, yet. */
  readonly style: 'solid' | 'dashed';
  /** Arrowheads at the end only (default) or at both ends. */
  readonly ends?: 'end' | 'both';
  readonly label?: LoopEdgeLabel;
  readonly from?: string;
  readonly to?: string;
}

interface PillarLoopBase {
  viewBox: { readonly w: number; readonly h: number };
  nodes: readonly LoopNode[];
  edges: readonly LoopEdge[];
  /** Prefixes the marker ids, so two loops on a page cannot collide. */
  idPrefix: string;
  /** Below `lg`: hide the map (default) or replace it with an ordered list. */
  collapse?: 'hide' | 'list';
  className?: string;
}

export type PillarLoopProps = PillarLoopBase &
  (
    | { mode: 'static'; selected?: undefined; onSelect?: undefined; renderDetail?: undefined; label?: undefined }
    | {
        mode: 'interactive';
        selected?: string;
        onSelect?: (id: string) => void;
        /** The panel under the map: what the selected node receives and hands on, in words. */
        renderDetail?: (id: string) => ReactNode;
        /** The node group's accessible name. */
        label: string;
        /**
         * `linked` (default): every edge touching the selected node is lit alike.
         * `direction`: outgoing edges in brand, incoming in text colour. Slice 71.
         */
        emphasis?: 'linked' | 'direction';
        /** One edge id drawn as data moving right now (white, dashed, marching when motion is allowed). Slice 71. */
        activeEdge?: string;
      }
  );

const NODE_STATE: Readonly<Record<LoopNodeState, string>> = {
  available: 'border-brand-b shadow-brand',
  conditional: 'border-line-strong',
  later: 'border-dashed border-line-strong',
  foundation: 'border-brand-b/30 bg-linear-to-r from-brand-a/16 to-brand-a/8',
};

const pct = (value: number, total: number): string => `${(value / total) * 100}%`;

const LABEL_ANCHOR = {
  start: '-translate-y-1/2',
  middle: '-translate-x-1/2 -translate-y-1/2',
  end: '-translate-x-full -translate-y-1/2',
} as const;

type EdgeLook = 'solid' | 'dashed' | 'on' | 'in' | 'active';

const EDGE_STROKE: Readonly<Record<EdgeLook, string>> = {
  solid: 'stroke-brand-b',
  dashed: 'stroke-muted',
  on: 'stroke-brand-b',
  in: 'stroke-text',
  active: 'stroke-white',
};

const MARKER_FILL: Readonly<Record<EdgeLook, string>> = {
  solid: 'fill-brand-b',
  dashed: 'fill-muted',
  on: 'fill-brand-b',
  in: 'fill-text',
  active: 'fill-white',
};

/** Slice 71 looks only: the stroke width and the label skin that go with them. */
const OPT_IN_WIDTH: Readonly<Partial<Record<EdgeLook, number>>> = { on: 2.6, in: 2.4, active: 2.6 };
const OPT_IN_LABEL: Readonly<Partial<Record<EdgeLook, string>>> = {
  on: 'border-brand-b text-brand-ink-strong',
  in: 'border-text/50 text-text',
  active: 'border-white text-white',
};
const MARCH_KEYFRAMES = '@keyframes wend-loop-march{to{stroke-dashoffset:-28}}';

function NodeFace({ node, selected }: { node: LoopNode; selected: boolean }) {
  // Name first in the DOM, kicker drawn first: see the header.
  return (
    <>
      <span className="flex items-center gap-[0.45em] text-[clamp(0.9rem,1.3vw,1.06rem)] font-semibold leading-tight tracking-[-0.01em] text-text">
        {node.glyph !== undefined && (
          <span aria-hidden="true" className={cn('leading-none', selected ? 'text-white' : 'text-brand-b')}>
            {node.glyph}
          </span>
        )}
        {node.name}
      </span>
      <Kicker as="span" tone={node.state === 'available' || selected ? 'brand' : 'muted'} className="order-first">
        {node.kicker}
      </Kicker>
      {node.line !== undefined && (
        <span className="line-clamp-2 text-[0.74rem] leading-snug text-muted">{node.line}</span>
      )}
    </>
  );
}

export function PillarLoop(props: PillarLoopProps) {
  const { viewBox, nodes, edges, idPrefix, collapse = 'hide', className } = props;
  const interactive = props.mode === 'interactive';
  const selected = props.mode === 'interactive' ? props.selected : undefined;
  const directional = props.mode === 'interactive' && props.emphasis === 'direction';
  const activeEdge = props.mode === 'interactive' ? props.activeEdge : undefined;
  /** Whether this render uses any slice 71 look — if not, the markup is exactly the slice 66 markup. */
  const optedIn = directional || activeEdge !== undefined;
  const groupRef = useRef<HTMLDivElement>(null);

  const linked = (id: string): boolean =>
    selected !== undefined &&
    edges.some((edge) => (edge.from === selected && edge.to === id) || (edge.to === selected && edge.from === id));

  const edgeLook = (edge: LoopEdge): EdgeLook => {
    if (activeEdge !== undefined && edge.id === activeEdge) return 'active';
    if (selected === undefined) return edge.style;
    if (directional) {
      if (edge.from === selected) return 'on';
      if (edge.to === selected) return 'in';
      return edge.style;
    }
    return edge.from === selected || edge.to === selected ? 'on' : edge.style;
  };

  const edgeDimmed = (edge: LoopEdge): boolean =>
    selected !== undefined && edge.from !== selected && edge.to !== selected && edge.id !== activeEdge;

  const markerId = (look: EdgeLook): string => `${idPrefix}-arrow-${look}`;
  const detailId = `${idPrefix}-detail`;
  const controls = props.mode === 'interactive' && props.renderDetail !== undefined ? detailId : undefined;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    // Only the copy on screen: below lg the map is display:none, from lg the list is.
    const buttons = Array.from(groupRef.current?.querySelectorAll<HTMLButtonElement>('[data-loop-node]') ?? []).filter(
      (button) => button.offsetParent !== null,
    );
    const current = buttons.findIndex((button) => button === document.activeElement);
    if (current === -1 || buttons.length === 0) return;
    event.preventDefault();
    const last = buttons.length - 1;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowRight' || event.key === 'ArrowDown'
            ? (current + 1) % buttons.length
            : (current - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  const nodeClass = (node: LoopNode): string => {
    const isSelected = selected === node.id;
    return cn(
      'absolute flex flex-col justify-center gap-[3px] overflow-hidden rounded-base border bg-surface-1 px-3.5 py-2.5 text-left',
      NODE_STATE[node.state],
      selected !== undefined && !isSelected && linked(node.id) && 'border-brand-b/45',
      isSelected &&
        'border-brand-b bg-linear-to-b from-brand-a/32 to-brand-b/8 shadow-selected forced-colors:outline-2 forced-colors:outline-offset-2',
      interactive && cn('cursor-pointer transition-[border-color,box-shadow] duration-200 hover:border-brand-b/60', focusRing),
    );
  };

  const map = (
    <div
      className="relative hidden w-full rounded-lg border border-line bg-surface-1 bg-dot-grid shadow-lift lg:block"
      style={{ aspectRatio: `${viewBox.w} / ${viewBox.h}` }}
    >
      <svg
        viewBox={`0 0 ${viewBox.w} ${viewBox.h}`}
        aria-hidden="true"
        focusable="false"
        className="pointer-events-none absolute inset-0 size-full overflow-visible"
      >
        {activeEdge !== undefined && <style>{MARCH_KEYFRAMES}</style>}
        <defs>
          {(optedIn ? (['solid', 'dashed', 'on', 'in', 'active'] as const) : (['solid', 'dashed', 'on'] as const)).map((look) => (
            <marker
              key={look}
              id={markerId(look)}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" className={MARKER_FILL[look]} />
            </marker>
          ))}
        </defs>
        {edges.map((edge) => {
          const look = edgeLook(edge);
          const marker = `url(#${markerId(look)})`;
          const active = look === 'active';
          return (
            <path
              key={edge.id}
              d={edge.d}
              fill="none"
              strokeWidth={optedIn ? (OPT_IN_WIDTH[look] ?? 2) : 2}
              strokeLinecap="round"
              strokeDasharray={active ? '7 7' : edge.style === 'dashed' ? '3 7' : undefined}
              markerEnd={marker}
              markerStart={edge.ends === 'both' ? marker : undefined}
              className={cn(
                EDGE_STROKE[look],
                'transition-opacity duration-200',
                edgeDimmed(edge) && 'opacity-35',
                active && 'motion-safe:animate-[wend-loop-march_0.9s_linear_infinite]',
              )}
            />
          );
        })}
      </svg>

      {nodes.map((node) => {
        const style = {
          left: pct(node.box.x, viewBox.w),
          top: pct(node.box.y, viewBox.h),
          width: pct(node.box.w, viewBox.w),
          height: pct(node.box.h, viewBox.h),
        };
        return interactive ? (
          <button
            key={node.id}
            type="button"
            data-loop-node={node.id}
            aria-pressed={selected === node.id}
            aria-controls={controls}
            onClick={() => (props.mode === 'interactive' ? props.onSelect?.(node.id) : undefined)}
            className={nodeClass(node)}
            style={style}
          >
            <NodeFace node={node} selected={selected === node.id} />
          </button>
        ) : (
          <div key={node.id} className={nodeClass(node)} style={style}>
            <NodeFace node={node} selected={false} />
          </div>
        );
      })}

      {edges.map((edge) => {
        if (edge.label === undefined) return null;
        const solid = edge.style === 'solid';
        const litSkin = optedIn ? OPT_IN_LABEL[edgeLook(edge)] : undefined;
        return (
          <span
            key={edge.id}
            className={cn(
              'pointer-events-none absolute whitespace-nowrap rounded-full border bg-surface-1 px-2 py-0.5 font-mono text-[0.68rem] leading-normal',
              LABEL_ANCHOR[edge.label.anchor ?? 'middle'],
              litSkin ?? (solid ? 'border-brand-b/60 text-text' : 'border-dashed border-line-strong text-muted'),
              // A solid label dims with its edge; a dashed one does not, because
              // muted text at 60% falls to 3.27:1 (slice 66 review).
              solid && edgeDimmed(edge) && 'opacity-60',
            )}
            style={{ left: pct(edge.label.x, viewBox.w), top: pct(edge.label.y, viewBox.h) }}
          >
            {edge.label.text}
          </span>
        );
      })}
    </div>
  );

  const nameOf = (id: string | undefined): string => nodes.find((node) => node.id === id)?.name ?? '';

  const list =
    collapse === 'list' ? (
      <ol className="grid gap-0 lg:hidden">
        {nodes.map((node) => {
          const outgoing = edges.filter((edge) => edge.from === node.id);
          const isSelected = selected === node.id;
          const face = <NodeFace node={node} selected={isSelected} />;
          const listNodeClass = cn(nodeClass(node), 'relative min-h-[74px] w-full');
          return (
            <li key={node.id} className="flex flex-col items-stretch">
              {interactive ? (
                <button
                  type="button"
                  data-loop-node={node.id}
                  aria-pressed={isSelected}
                  aria-controls={controls}
                  onClick={() => (props.mode === 'interactive' ? props.onSelect?.(node.id) : undefined)}
                  className={listNodeClass}
                >
                  {face}
                </button>
              ) : (
                <div className={listNodeClass}>{face}</div>
              )}
              {outgoing.map((edge) => (
                <p key={edge.id} className="self-center py-2 font-mono text-[0.66rem] text-muted">
                  <span aria-hidden="true" className="mr-1 text-brand-ink">
                    ↓
                  </span>
                  {edge.label !== undefined ? `${edge.label.text} · ` : ''}
                  to {nameOf(edge.to)}
                </p>
              ))}
            </li>
          );
        })}
      </ol>
    ) : null;

  if (props.mode === 'static') {
    return (
      <div aria-hidden="true" className={className}>
        {map}
        {list}
      </div>
    );
  }

  return (
    <div className={className}>
      <div ref={groupRef} role="group" aria-label={props.label} onKeyDown={onKeyDown}>
        {map}
        {list}
      </div>
      {props.renderDetail !== undefined && (
        <div id={detailId} aria-live="polite" className="mt-4">
          {selected !== undefined ? props.renderDetail(selected) : null}
        </div>
      )}
    </div>
  );
}
